import React from 'react';

import { CommentCard, MenuItem } from '@change/ds-core-ui';
import { ReportIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useOpenReportAbuseModal } from 'src/app/shared/components/abuseSunrise';

import type { SupporterComment, SupporterCommentPetition } from '../../shared/types';

import { useCommentLikes } from './hooks/useCommentLikes';
import { useReportComment } from './hooks/useReportComment';

export const SupporterCommentCard: React.FC<SupporterCommentCardProps> = ({ supporterComment, petition }) => {
	const { translate } = useI18n();

	const {
		data: { liked, a11yLabel: likeButtonAriaLabel, likesCount, likes },
		actions: { toggleLiked },
	} = useCommentLikes({ comment: supporterComment });

	const {
		data: { canReportComment, a11yLabel: reportAriaLabel },
		actions: { handleReportClick },
	} = useReportComment({ petitionId: petition.id, comment: supporterComment });

	const reportLabel = translate('fe.components.comments.report');

	const { open: openReportAbuseModal, reportAbuseModals } = useOpenReportAbuseModal({
		reportContext: {
			type: 'supporter_comment',
			comment: {
				id: supporterComment.id,
				likes: supporterComment.likes,
				role: supporterComment.role,
			},
			petitionId: petition.id,
			petitionSlug: petition.slug,
		},
	});

	const handleReportAction = () => {
		handleReportClick();
		openReportAbuseModal();
	};

	const moreActions: React.ComponentProps<typeof CommentCard>['moreActions'] = [
		<MenuItem
			key="report-comment-menu-item"
			className="[&_svg]:h-4 [&_svg]:w-4"
			onAction={handleReportAction}
			leadingIcon={<ReportIcon />}
			label={reportLabel}
			aria-label={reportAriaLabel}
			data-testid={`supporter-comment-${supporterComment.id}-report-button`}
		/>,
	];

	const moreActionsAriaLabel = translate('corgi.components.petition_video.actions.aria_label.more_actions');
	const moreActionProps: LocalWithActions | LocalWithoutActions = canReportComment
		? {
				moreActions,
				moreActionsAriaLabel,
			}
		: {};
	return (
		<div data-testid={`supporter-comment-${supporterComment.id}`}>
			<CommentCard
				commentContent={supporterComment.comment}
				maxCommentLines={3}
				userLocation={supporterComment.user.city ?? undefined}
				userName={supporterComment.user.shortDisplayName}
				liked={liked}
				likeCounter={likesCount === 0 ? undefined : likes}
				likeButtonAriaLabel={likeButtonAriaLabel}
				likeButtonDataQa="comment-toggle-like"
				onLikeChanged={toggleLiked}
				fullCommentLinkLabel={translate('corgi.petition_gamma.details.description_show_full_text')}
				{...moreActionProps}
			/>
			{reportAbuseModals}
		</div>
	);
};

type SupporterCommentCardProps = {
	supporterComment: SupporterComment;
	petition: SupporterCommentPetition;
};

type LocalWithActions = {
	moreActions: NonNullable<React.ComponentProps<typeof CommentCard>['moreActions']>;
	moreActionsAriaLabel: string;
};

type LocalWithoutActions = Record<string, never>;
